import { initialize } from "store/create";
import { initializeGoogle } from "utils/search/autocomplete";
import {
	TrackingClient,
	captureAllClickIds,
	buildMetaEntries as buildMetaEntriesFromCapture
} from "@sparefoot/tracking-client";
import { ReactSegmentWrapper } from "@sparefoot/segment-react-wrapper";
import { saveBatchMeta } from "store/modules/user";

const MAX_RETRIES = 10;
const RETRY_DELAY_MS = 300;
const STORAGE_KEY = "sdc_tracking_meta_sent";

/**
 * Get sent keys from sessionStorage for the current visitId.
 * Returns empty Set if visitId changed or no data exists.
 */
function getSentKeysForCurrentVisit(visitId) {
	if (typeof sessionStorage === "undefined") {
		return new Set();
	}

	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) return new Set();

		const { vid, keys } = JSON.parse(stored);
		if (vid !== visitId) return new Set();

		return new Set(keys);
	} catch {
		return new Set();
	}
}

/**
 * Mark entries as sent for this visit only after a confirmed save.
 * Persists to sessionStorage so state survives page reloads within the session.
 * On failure, keys are intentionally left unmarked so a retry re-includes them.
 * Dedup is keyed on entry name, not value: first-touch-per-visit is the intended
 * attribution model, so a second click ID of the same type captured later in the
 * same visit is intentionally dropped rather than overwriting the first.
 */
function markEntriesAsSent(visitId, entries) {
	if (typeof sessionStorage === "undefined") return;

	try {
		const existingKeys = getSentKeysForCurrentVisit(visitId);
		entries.forEach(({ key }) => existingKeys.add(key));

		sessionStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				vid: visitId,
				keys: Array.from(existingKeys)
			})
		);
	} catch {
		// sessionStorage may be full or disabled - fail silently
	}
}

/**
 * Build array of meta entries that still need to be sent for this visit.
 * Uses the shared tracking-client capture functions for all click IDs.
 * @param {Object} user - Current user state
 * @returns {{ entries: Array, gcidValue: string|null }}
 */
function buildMetaEntries(user) {
	const captured = captureAllClickIds();
	const alreadySent = getSentKeysForCurrentVisit(user.visitId);
	const entries = buildMetaEntriesFromCapture(captured, alreadySent);

	return { entries, gcidValue: captured.google_client_id };
}

/**
 * Send meta entries to tracking service.
 * @param {Function} dispatch - Redux dispatch
 * @param {Array} entries - Meta entries to send
 * @param {string} visitId - Current visit ID for marking sent keys
 */
async function sendMetaEntries(dispatch, entries, visitId) {
	if (entries.length === 0) {
		return;
	}

	try {
		await dispatch(saveBatchMeta({ entries })).unwrap();
		markEntriesAsSent(visitId, entries);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error("Failed to save batch meta to tracking service", error);
		// Keys intentionally left unmarked so the next page load in this session re-includes them
	}
}

/**
 * Unified capture for all click IDs (google_client_id, gclid, gbraid, wbraid,
 * fbclid, fbc, fbp, msclkid).
 * Strategy: gcid gates the send (GA4 loads async). If gcid available, send immediately
 * with whatever other IDs we have. If not, retry up to MAX_RETRIES then send whatever we have.
 * sessionStorage tracks which keys have been sent for this visitId across page reloads.
 */
function captureTrackingMeta(dispatch, getState) {
	let retries = 0;

	function attemptCapture() {
		const { user } = getState();
		const { entries, gcidValue } = buildMetaEntries(user);

		// If we have gcid, send immediately with whatever other IDs we have
		if (gcidValue) {
			// Nothing new to send - all keys already sent for this visitId
			if (entries.length === 0) {
				return;
			}

			sendMetaEntries(dispatch, entries, user.visitId);
			return;
		}

		// No gcid yet - retry until we get it or exhaust retries
		if (retries < MAX_RETRIES) {
			retries += 1;
			setTimeout(attemptCapture, RETRY_DELAY_MS);
		} else if (entries.length > 0) {
			// Max retries reached - send whatever we have (likely just gclid if any)
			sendMetaEntries(dispatch, entries, user.visitId);
		}
	}

	attemptCapture();
}

/**
 * Reset tracking state. Exported for testing.
 */
export function resetTrackingMetaState() {
	if (typeof sessionStorage !== "undefined") {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch {
			// Ignore errors
		}
	}
}

// Export for testing
export {
	captureTrackingMeta,
	buildMetaEntries,
	getSentKeysForCurrentVisit,
	markEntriesAsSent
};

export default ({ dispatch, getState }) => {
	const { app, user } = getState();

	TrackingClient.initialize(
		new ReactSegmentWrapper({
			writeKey: app.segmentWriteKey,
			visitId: user.visitId,
			environment: app.environment,
			siteId: 424,
			cdnURL: app.eventsCdnUrl,
			siteType: "desktop",
			integrations: {
				"Segment.io": {
					apiHost: app.eventsApiUrl
				}
			},
			debug:
				process.env.NODE_ENV === "development" &&
				process.env.RENDER_ENV === "client"
		})
	);

	initializeGoogle({
		key: app.googleMapsKey
	});

	dispatch(initialize());

	// Capture tracking meta (google_client_id + gclid) for attribution (client-side only)
	if (typeof globalThis.window !== "undefined") {
		captureTrackingMeta(dispatch, getState);
	}
};
